import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const OCCUPATION_SEARCH_RESOURCE = "occupation" as const;
export const OCCUPATION_SEARCH_OPERATION = "search" as const;
export const OCCUPATION_SEARCH_API_PATH = `/v1/${OCCUPATION_SEARCH_RESOURCE}/${OCCUPATION_SEARCH_OPERATION}` as const;

export type OccupationSearchParameters = paths[typeof OCCUPATION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type OccupationSearchBody =
  paths[typeof OCCUPATION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type OccupationSearchResponse =
  paths[typeof OCCUPATION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useOccupationSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<OccupationSearchParameters, OccupationSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    OccupationSearchResponse,
    Error,
    OccupationSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<OccupationSearchParameters, OccupationSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      OCCUPATION_SEARCH_RESOURCE,
      OCCUPATION_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<OccupationSearchResponse>(
        OCCUPATION_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
